import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { gameState } from '../../truman/state.js';
import '../../styles/pages/GameStatusPanel.css';
import townMapData from '../../assets/map/town_map.json';
import AIChannel from './AIChannel';

// Dynamically import all SVG icons from the architecture directory
const svgIconModules = import.meta.glob('../../assets/map/architecture/*.svg', { eager: true });

// Create a mapping from location ID (filename without extension) to SVG URL or component
const locationIcons = Object.keys(svgIconModules).reduce((acc, path) => {
  const fileName = path.split('/').pop().replace('.svg', '');
  // Assuming default export is the URL or component based on Vite config (svgr plugin?)
  acc[fileName] = svgIconModules[path].default; 
  return acc;
}, {});


const GameStatusPanel = ({
  isPanelVisible, 
  togglePanel, 
  gameStatus, 
  refreshGameStatus, 
  locations, 
  getLocationName 
}) => {
  const { t } = useTranslation();
  
  // 当前选中的建筑物
  const [selectedLocation, setSelectedLocation] = useState(null);
  
  // 面板当前显示的标签页
  const [activeTab, setActiveTab] = useState('status'); // 'status', 'buildings', 'characters'
  
  // AI信息/消息状态
  const [messages, setMessages] = useState([]);
  
  // 监听handleMailInfo中的消息
  useEffect(() => {
    // 创建一个事件监听函数来接收新消息
    const handleNewMessage = (event) => {
      if (event && event.detail) {
        setMessages(prevMessages => {
          // 将新消息添加到数组开头（最新的消息显示在顶部）
          return [event.detail, ...prevMessages].slice(0, 50); // 只保留最近的50条消息
        });
      }
    };
    
    // 注册事件监听器
    window.addEventListener('mail_info', handleNewMessage);
    
    // 清理函数
    return () => {
      window.removeEventListener('mail_info', handleNewMessage);
    };
  }, []);
  
  // 游戏时间状态
  const [formattedGameTime, setFormattedGameTime] = useState('0 天 00:00:00');
  const [localSeconds, setLocalSeconds] = useState(0);
  const timerRef = useRef(null);
  
  // 解析时间字符串为Date对象
  const parseGameTime = (timeString) => {
    if (!timeString) return null;
    
    // 假设时间格式为 "YYYY-MM-DD HH:MM:SS"
    const [datePart, timePart] = timeString.split(' ');
    if (!datePart || !timePart) return null;
    
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second] = timePart.split(':').map(Number);
    
    return new Date(year, month - 1, day, hour, minute, second);
  };
  
  // 格式化时间差为天数格式
  const formatTimeDifference = (startDate, currentDate) => {
    if (!startDate || !currentDate) return '0 天 00:00:00';
    
    // 计算毫秒差
    let diffMs = currentDate.getTime() - startDate.getTime();
    if (diffMs < 0) diffMs = 0;
    
    // 计算天、时、分、秒
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    diffMs %= (1000 * 60 * 60 * 24);
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    diffMs %= (1000 * 60 * 60);
    
    const minutes = Math.floor(diffMs / (1000 * 60));
    diffMs %= (1000 * 60);
    
    const seconds = Math.floor(diffMs / 1000);
    
    // 格式化为 "X 天 HH:MM:SS"
    return `${days} 天 ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
  
  // 更新游戏时间
  useEffect(() => {
    // 清除旧计时器
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    // 解析游戏时间和起始时间
    const gameTimeDate = parseGameTime(gameStatus.game_time);
    const startTimeDate = parseGameTime(gameStatus.starting_time);
    
    if (gameTimeDate && startTimeDate) {
      // 设置初始格式化时间
      setFormattedGameTime(formatTimeDifference(startTimeDate, gameTimeDate));
      setLocalSeconds(0);
      
      // 设置定时器，每秒更新一次本地时间
      timerRef.current = setInterval(() => {
        setLocalSeconds(prev => {
          const newSeconds = prev + 1;
          
          // 创建一个新的日期对象，加上流逝的秒数
          const newDate = new Date(gameTimeDate.getTime() + newSeconds * 1000 * (gameStatus.time_flow_rate || 1));
          
          // 更新格式化的游戏时间
          setFormattedGameTime(formatTimeDifference(startTimeDate, newDate));
          
          return newSeconds;
        });
      }, 1000);
    }
    
    // 组件卸载时清除计时器
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [gameStatus.game_time, gameStatus.starting_time, gameStatus.time_flow_rate]);
  
  // 获取建筑物的中文名称和描述
  const getLocationInfo = (location) => {
    if (!location) return { name: '', description: '' };
    
    // 尝试在地标中查找
    const landmark = townMapData.landmarks.find(lm => lm.id === location.id);
    if (landmark) {
      return {
        name: landmark.name || location.name,
        description: landmark.description || location.description
      };
    }
    
    // 如果在地标中找不到，则尝试在区域中查找
    const area = Object.values(townMapData.areas).find(a => 
      a.name === location.name || a.center.x === location.position?.x && a.center.y === location.position?.y
    );
    if (area) {
      return {
        name: area.name || location.name,
        description: area.description || location.description
      };
    }
    
    // 如果都找不到，返回原始名称和描述
    return {
      name: location.name,
      description: location.description
    };
  };
  
  // 通过ID获取地点的中文名称
  const getLocationNameById = (locationId) => {
    // 首先在地标中查找
    const landmark = townMapData.landmarks.find(lm => lm.id === locationId);
    if (landmark) {
      return landmark.name;
    }
    
    // 然后在区域中查找
    const areaKey = Object.keys(townMapData.areas).find(key => key === locationId);
    if (areaKey) {
      return townMapData.areas[areaKey].name;
    }
    
    // 在locations中查找
    const location = locations.find(loc => loc.id === locationId);
    if (location) {
      return getLocationInfo(location).name;
    }
    
    // 如果都找不到，返回原始ID
    return locationId;
  };
  
  // 根据建筑物类型获取颜色
  const getTypeColor = (type) => {
    const typeColors = {
      'residence': '#8BC34A',    // 绿色
      'workplace': '#42A5F5',    // 蓝色
      'social': '#FF9800',       // 橙色
      'public': '#9C27B0',       // 紫色
      'creative': '#E91E63',     // 粉色
      'wonder': '#00BCD4',       // 青色
      'commerce': '#FFC107',     // 琥珀色
      'mystery': '#673AB7',      // 深紫色
      'knowledge': '#3F51B5',    // 靛蓝色
      'restoration': '#4CAF50',  // 绿色
      'challenge': '#F44336'     // 红色
    };
    
    return typeColors[type] || '#9E9E9E'; // 默认灰色
  };

  // Helper function to render the building icon
  const renderBuildingIcon = (locationId, locationType) => {
    const IconSource = locationIcons[locationId];

    if (IconSource) {
      // Check if it's a React component (exported by svgr plugin)
      if (typeof IconSource === 'function' || (typeof IconSource === 'object' && IconSource.$$typeof)) {
        // Render as component
        return <IconSource className="building-svg-icon" />;
      } else if (typeof IconSource === 'string') {
        // Render as img if it's a URL string
        return <img src={IconSource} alt={`${locationId} icon`} className="building-svg-icon" />;
      }
    }
    
    // Fallback to original letter icon if SVG not found or not renderable
    return (
      <div className="building-icon" style={{ backgroundColor: getTypeColor(locationType) }}>
        {locationType.charAt(0).toUpperCase()}
      </div>
    );
  };

  return (
    <>
      {/* 游戏面板触发按钮 */}
      <div 
        className={`game-panel-trigger ${isPanelVisible ? 'active' : ''}`}
        onClick={togglePanel}
        title={isPanelVisible ? "收起面板" : "展开面板"}
      >
        <i>{isPanelVisible ? '✕' : '☰'}</i>
      </div>
      
      {/* 游戏数据面板 - 覆盖在地图上，不挤压地图 */}
      <div className={`game-status-panel ${isPanelVisible ? 'visible' : ''}`}>
        <div className="panel-header">
          <h3>平面小镇-游戏状态面板</h3>
          <div className="refresh-button" onClick={refreshGameStatus}>⟳</div>
          <div className="close-button" onClick={togglePanel}>×</div>
        </div>
        
        {/* 面板标签页导航 */}
        <div className="panel-tabs">
          <div 
            className={`panel-tab ${activeTab === 'status' ? 'active' : ''}`}
            onClick={() => setActiveTab('status')}
          >
            游戏状态
          </div>
          <div 
            className={`panel-tab ${activeTab === 'buildings' ? 'active' : ''}`}
            onClick={() => setActiveTab('buildings')}
          >
            建筑物
          </div>
          <div 
            className={`panel-tab ${activeTab === 'characters' ? 'active' : ''}`}
            onClick={() => setActiveTab('characters')}
          >
            角色
          </div>
        </div>
        
        {/* 游戏状态标签页 */}
        {activeTab === 'status' && (
          <div className="panel-section game-status-section">
            <div className="game-time-display ghibli-style">
              {formattedGameTime}
            </div>
            
            {/* AI消息频道 */}
            <AIChannel messages={messages} />
          </div>
        )}
        
        {/* 建筑物标签页 */}
        {activeTab === 'buildings' && (
          <div className="panel-section">
            <h4>地图建筑物 ({locations.length})</h4>
            
            {selectedLocation ? (
              <div className="building-details">
                <div className="building-details-header">
                  <h5>{getLocationInfo(selectedLocation).name}</h5>
                  <button 
                    className="back-button"
                    onClick={() => setSelectedLocation(null)}
                  >
                    返回
                  </button>
                </div>
                
                <div className="building-info">
                  <div className="info-row">
                    <span className="info-label">类型:</span>
                    <span className="info-value">{selectedLocation.type}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">位置:</span>
                    <span className="info-value">({selectedLocation.position.x}, {selectedLocation.position.y})</span>
                  </div>
                  <div className="info-row description">
                    <span className="info-label">描述:</span>
                    <div className="info-value description-text">{getLocationInfo(selectedLocation).description}</div>
                  </div>
                  
                  {selectedLocation.available_actions && selectedLocation.available_actions.length > 0 && (
                    <div className="info-row">
                      <span className="info-label">可用操作:</span>
                      <div className="info-value actions-list">
                        {selectedLocation.available_actions.map((action, index) => (
                          <span key={index} className="action-tag">{action}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {selectedLocation.associated_events && selectedLocation.associated_events.length > 0 && (
                    <div className="info-row">
                      <span className="info-label">相关事件:</span>
                      <div className="info-value events-list">
                        {selectedLocation.associated_events.map((event, index) => (
                          <span key={index} className="event-tag">{event}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {selectedLocation.neighbors && selectedLocation.neighbors.length > 0 && (
                    <div className="info-row">
                      <span className="info-label">相邻地点:</span>
                      <div className="info-value neighbors-list">
                        {selectedLocation.neighbors.map((neighbor, index) => (
                          <span 
                            key={index} 
                            className="neighbor-tag"
                            onClick={() => {
                              const neighborLocation = locations.find(loc => loc.id === neighbor);
                              if (neighborLocation) {
                                setSelectedLocation(neighborLocation);
                              }
                            }}
                          >
                            {getLocationNameById(neighbor)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="buildings-list">
                {locations.map((location, index) => (
                  <div 
                    key={location.id || index} 
                    className="building-item"
                    onClick={() => setSelectedLocation(location)}
                  >
                    {/* Use the helper function to render the icon */}
                    {renderBuildingIcon(location.id, location.type)}
                    <div className="building-info">
                      <div className="building-name">{getLocationInfo(location).name}</div>
                      <div className="building-type">{location.type}</div>
                    </div>
                    <div className="building-arrow">›</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* 角色标签页 */}
        {activeTab === 'characters' && (
          <div className="panel-section">
            <h4>地图角色 ({gameState.characters ? gameState.characters.length : 0})</h4>
            <div className="characters-list">
              {gameState.characters && gameState.characters.map((character, index) => (
                <div key={character.id || index} className="character-item">
                  <div className="character-avatar" style={{ backgroundColor: character.color || '#ccc' }}>
                    {character.name ? character.name.charAt(0) : '?'}
                  </div>
                  <div className="character-info">
                    <div className="character-name">{character.name || '未知角色'}</div>
                    <div className="character-location">{character.position ? getLocationName(character.position.x, character.position.y) : '未知位置'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default GameStatusPanel;
